/**
 * @ai-content-hub/content-elements — UI i18n
 *
 * Self-contained translations for element UI chrome (edit labels, player empty
 * states, viewer navigation). Element *content* strings are handled per-schema
 * via `useElementSchemaLocale`.
 *
 * Supports `en` and `vi`.
 */

export interface ElementUITranslations {
    // Page edit
    block: string;
    section: string;
    sectionTitle: string;
    sectionTitlePlaceholder: string;
    layout: string;
    contentBlocks: string;

    // Office document edit
    titleOptional: string;
    documentTitlePlaceholder: string;

    // Image hotspot edit
    imageAndHotspots: string;
    label: string;
    hotspotLabelPlaceholder: string;
    resize: string;
    changeBackgroundImage: string;
    hotspotContent: string;

    // Image hotspot edit actions
    changeBackground: string;
    removeBackground: string;
    addHotspot: string;
    editContent: string;
    uploadBackgroundFirst: string;

    // Player empty / error states
    noImageData: string;
    noImageSelected: string;
    loadingImage: string;
    loadingVideo: string;
    loadingAudio: string;
    loadingMedia: string;
    noMediaSelected: string;
    noRichTextData: string;
    noContent: string;
    previewFailed: string;
    cannotRenderWord: string;
    /** The download itself failed (network error, or a non-2xx response). */
    documentDownloadFailed: string;
    /** The download succeeded but the body was an error/login/SPA page, not the document. */
    documentServerError: string;
    /** The body really was a PDF, and the parser still could not read it. */
    documentPdfDamaged: string;
    /** The PDF is encrypted and needs a password we do not have. */
    documentPdfPasswordProtected: string;

    // Viewer navigation
    previousSlide: string;
    nextSlide: string;

    // Block type selector
    media: string;
    mediaTitle: string;

    // MCQ edit
    feedbackPlaceholder: string;

    // Upload / general
    uploading: string;
    uploadFile: string;
    documentUrl: string;
}

const en: ElementUITranslations = {
    block: 'Block',
    section: 'Section',
    sectionTitle: 'Section Title',
    sectionTitlePlaceholder: 'Optional section title',
    layout: 'Layout',
    contentBlocks: 'Content Blocks',

    titleOptional: 'Title (optional)',
    documentTitlePlaceholder: 'Document title',

    imageAndHotspots: 'Image & Hotspots',
    label: 'Label',
    hotspotLabelPlaceholder: 'Hotspot label',
    resize: 'Resize',
    changeBackgroundImage: 'Change background image',
    hotspotContent: 'Hotspot content',

    changeBackground: 'Change background',
    removeBackground: 'Remove background',
    addHotspot: 'Add Hotspot',
    editContent: 'Edit content',
    uploadBackgroundFirst: 'Upload a background image first',

    noImageData: 'No image data',
    noImageSelected: 'No image selected',
    loadingImage: 'Loading image…',
    loadingVideo: 'Loading video…',
    loadingAudio: 'Loading audio…',
    loadingMedia: 'Loading media…',
    noMediaSelected: 'No {mediaType} selected',
    noRichTextData: 'No rich text data',
    noContent: 'No content',
    previewFailed: 'Preview failed for this document.',
    cannotRenderWord: 'Could not render this Word file.',
    documentDownloadFailed: 'Could not download this file for preview.',
    documentServerError:
        'The server did not return this document. Please reload the page, and contact support if it keeps happening.',
    documentPdfDamaged: 'Could not open this PDF file — the file appears to be damaged.',
    documentPdfPasswordProtected: 'This PDF is password-protected, so it cannot be shown here.',

    previousSlide: 'Previous slide',
    nextSlide: 'Next slide',

    media: 'Media',
    mediaTitle: 'Media (Image, Audio, Video)',

    feedbackPlaceholder: 'Feedback for this choice...',

    uploading: 'Uploading…',
    uploadFile: 'Upload file',
    documentUrl: 'Document URL',
};

const vi: ElementUITranslations = {
    block: 'Khối',
    section: 'Phần',
    sectionTitle: 'Tiêu đề phần',
    sectionTitlePlaceholder: 'Tiêu đề phần (tùy chọn)',
    layout: 'Bố cục',
    contentBlocks: 'Khối nội dung',

    titleOptional: 'Tiêu đề (tùy chọn)',
    documentTitlePlaceholder: 'Tiêu đề tài liệu',

    imageAndHotspots: 'Hình ảnh & Điểm nóng',
    label: 'Nhãn',
    hotspotLabelPlaceholder: 'Nhãn điểm nóng',
    resize: 'Thay đổi kích thước',
    changeBackgroundImage: 'Đổi hình nền',
    hotspotContent: 'Nội dung điểm nóng',

    changeBackground: 'Đổi hình nền',
    removeBackground: 'Xóa hình nền',
    addHotspot: 'Thêm điểm nóng',
    editContent: 'Sửa nội dung',
    uploadBackgroundFirst: 'Tải hình nền lên trước',

    noImageData: 'Không có dữ liệu hình ảnh',
    noImageSelected: 'Chưa chọn hình ảnh',
    loadingImage: 'Đang tải hình ảnh…',
    loadingVideo: 'Đang tải video…',
    loadingAudio: 'Đang tải âm thanh…',
    loadingMedia: 'Đang tải phương tiện…',
    noMediaSelected: 'Chưa chọn {mediaType}',
    noRichTextData: 'Không có dữ liệu văn bản',
    noContent: 'Không có nội dung',
    previewFailed: 'Không thể xem trước tài liệu này.',
    cannotRenderWord: 'Không thể hiển thị tệp Word này.',
    documentDownloadFailed: 'Không tải được tệp này để xem trước.',
    documentServerError:
        'Máy chủ không trả về tài liệu này. Vui lòng tải lại trang, nếu vẫn lỗi hãy liên hệ bộ phận hỗ trợ.',
    documentPdfDamaged: 'Không mở được tệp PDF này — tệp có thể đã bị hỏng.',
    documentPdfPasswordProtected: 'Tệp PDF này được đặt mật khẩu nên không thể hiển thị ở đây.',

    previousSlide: 'Trang chiếu trước',
    nextSlide: 'Trang chiếu tiếp',

    media: 'Phương tiện',
    mediaTitle: 'Phương tiện (Hình ảnh, Âm thanh, Video)',

    feedbackPlaceholder: 'Phản hồi cho lựa chọn này...',

    uploading: 'Đang tải lên…',
    uploadFile: 'Tải lên tệp',
    documentUrl: 'URL tài liệu',
};

const translations: Record<string, ElementUITranslations> = { en, vi };

let currentLocale = 'en';

/** Set the global locale for all element UI components. */
export function setElementUILocale(locale: string): void {
    const normalized = locale?.toLowerCase().startsWith('vi') ? 'vi' : 'en';
    currentLocale = normalized;
}

/** Get the current global locale. */
export function getElementUILocale(): string {
    return currentLocale;
}

/** Get translations for the given locale (or current global locale). */
export function getElementUITranslations(locale?: string): ElementUITranslations {
    if (locale) {
        const normalized = locale.toLowerCase().startsWith('vi') ? 'vi' : 'en';
        return translations[normalized] || translations.en;
    }
    return translations[currentLocale] || translations.en;
}

/** React hook — returns translations for the current or given locale. */
export function useElementUITranslations(locale?: string): ElementUITranslations {
    return getElementUITranslations(locale);
}
